// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { zodResolver } from '@hookform/resolvers/zod';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import { buildDocumentMetadataFromForm } from '@shared/data-access/transformers/documents/build-document-metadata-from-form';
import type {
  Document,
  DocumentEntityType,
} from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { useConfirmPopup } from '@shared/ui-kit/components';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDocumentCatalog } from '../../hooks/use-document-catalog';
import { useDocumentsClientId } from '../../hooks/use-documents-client-id';
import {
  createUploadDocumentFormSchema,
  defaultUploadDocumentFormValues,
  type UploadDocumentFormData,
} from '../../types/upload-document-form';
import { formatDocumentFileSize } from '../../utils/format-document-file-size';
import {
  mergeUploadFiles,
  sumFileSizes,
  UPLOAD_DOCUMENT_MAX_BATCH_BYTES,
  type UploadFileValidationError,
} from '../../utils/validate-upload-files';

export interface UploadDocumentLinkToEntity {
  readonly entityType: DocumentEntityType;
  readonly entityId: string;
}

export interface UseUploadDocumentDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: (document: Document) => void;
  readonly linkToEntity?: UploadDocumentLinkToEntity;
  readonly hideBackdrop?: boolean;
}

export const useUploadDocumentDrawer = ({
  open,
  onClose,
  onSuccess,
  linkToEntity,
}: UseUploadDocumentDrawerProps) => {
  const { t } = useTranslation();
  const clientId = useDocumentsClientId();
  const {
    personalCategories,
    companyCategories,
    getCategoriesForScope,
    getSubcategoriesForCategory,
    currencies,
    languages,
  } = useDocumentCatalog();
  const showConfirm = useConfirmPopup();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [DrawerRenderer, drawerControls] = useDrawer({ open, onClose });

  const uploadDocumentFormSchema = useMemo(
    () => createUploadDocumentFormSchema(t),
    [t],
  );

  const form = useForm<UploadDocumentFormData>({
    resolver: zodResolver(uploadDocumentFormSchema),
    defaultValues: defaultUploadDocumentFormValues(),
    mode: 'onChange',
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<UploadFileValidationError | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createDocument] = documentsApi.useCreateDocumentMutation();
  const [updateDocument] = documentsApi.useUpdateDocumentMutation();

  const resetUploadState = useCallback(() => {
    setSelectedFiles([]);
    setFileError(null);
    form.reset(defaultUploadDocumentFormValues());
    form.clearErrors();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [form]);

  useEffect(() => {
    if (open) {
      resetUploadState();
      setIsSubmitting(false);
    }
  }, [open, resetUploadState]);

  const formValues = form.watch();
  const isFormValid = useMemo(
    () => uploadDocumentFormSchema.safeParse(formValues).success,
    [formValues, uploadDocumentFormSchema],
  );

  const isDirty = form.formState.isDirty || selectedFiles.length > 0;

  const isSaveDisabled = selectedFiles.length === 0 || !isFormValid || isSubmitting;

  const batchSizeLabel = formatDocumentFileSize(sumFileSizes(selectedFiles));
  const batchLimitLabel = formatDocumentFileSize(UPLOAD_DOCUMENT_MAX_BATCH_BYTES);

  const getFileErrorMessage = useCallback(
    (error: UploadFileValidationError | null) => {
      if (!error) {
        return null;
      }

      return t(`documents.upload-drawer.file-error.${error}`, { ns: 'components' });
    },
    [t],
  );

  const applyFiles = useCallback(
    (incoming: FileList | null) => {
      const result = mergeUploadFiles(selectedFiles, incoming);

      if (!result.valid) {
        setFileError(result.error ?? 'invalid_type');
        return;
      }

      setFileError(null);
      setSelectedFiles(result.validFiles);
    },
    [selectedFiles],
  );

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      applyFiles(event.target.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [applyFiles],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      applyFiles(event.dataTransfer.files);
    },
    [applyFiles],
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onSelectFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onRemoveFile = useCallback((index: number) => {
    setSelectedFiles(current =>
      current.filter((_, fileIndex) => fileIndex !== index),
    );
    setFileError(null);
  }, []);

  const closeDrawer = useCallback(() => {
    resetUploadState();
    onClose();
  }, [onClose, resetUploadState]);

  const onRequestClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    if (!isDirty) {
      closeDrawer();
      return;
    }

    showConfirm({
      title: t('documents.upload-drawer.discard-confirm-title', {
        ns: 'components',
      }),
      message: t('documents.upload-drawer.discard-confirm-message', {
        ns: 'components',
      }),
      confirmText: t('documents.upload-drawer.discard-confirm-action', {
        ns: 'components',
      }),
      cancelText: t('common.cancel', { ns: 'common' }),
      onConfirm: closeDrawer,
    });
  }, [closeDrawer, isDirty, isSubmitting, showConfirm, t]);

  const onScopeChange = useCallback(
    (scope: DocumentScope) => {
      form.setValue('scope', scope, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const category = form.getValues('category');
      const subcategory = form.getValues('subcategory');

      if (
        category &&
        !getCategoriesForScope(scope).some(option => option.value === category)
      ) {
        form.setValue('category', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.setValue('subcategory', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }

      if (
        subcategory &&
        category &&
        !getSubcategoriesForCategory(scope, category).some(
          option => option.value === subcategory,
        )
      ) {
        form.setValue('subcategory', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [form, getCategoriesForScope, getSubcategoriesForCategory],
  );

  const onCategoryChange = useCallback(
    (category: string) => {
      form.setValue('category', category, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const scope = form.getValues('scope');
      const subcategory = form.getValues('subcategory');

      if (
        subcategory &&
        !getSubcategoriesForCategory(scope, category).some(
          option => option.value === subcategory,
        )
      ) {
        form.setValue('subcategory', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [form, getSubcategoriesForCategory],
  );

  const submitUpload = useCallback(
    async (values: UploadDocumentFormData) => {
      if (selectedFiles.length === 0) {
        return;
      }

      const uploadingMessage = values.title.trim();

      const uploadingKey = enqueueSnackbar(uploadingMessage, {
        variant: 'uploadingDocument',
        persist: true,
      });

      setIsSubmitting(true);

      try {
        const metadata = buildDocumentMetadataFromForm(values, { clientId });
        const created = await createDocument({
          body: {
            metadata: JSON.stringify(metadata),
            files: selectedFiles,
          },
        }).unwrap();

        if (linkToEntity?.entityId) {
          try {
            await updateDocument({
              document_id: created.id,
              body: {
                relations_to_add: [
                  {
                    entity_type: linkToEntity.entityType,
                    entity_id: linkToEntity.entityId,
                  },
                ],
              },
            }).unwrap();
          } catch {
            closeSnackbar(uploadingKey);
            enqueueSnackbar(
              t('documents.entity-section.link-error', { ns: 'components' }),
              { variant: 'error' },
            );
            closeDrawer();
            onSuccess?.(created);
            return;
          }
        }

        closeSnackbar(uploadingKey);
        enqueueSnackbar(
          t('documents.upload-drawer.success', {
            ns: 'components',
            title: created.title,
          }),
          { variant: 'success' },
        );
        closeDrawer();
        onSuccess?.(created);
      } catch {
        closeSnackbar(uploadingKey);
        enqueueSnackbar(
          t('documents.upload-drawer.submit-error', { ns: 'components' }),
          { variant: 'error' },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      clientId,
      closeDrawer,
      closeSnackbar,
      createDocument,
      enqueueSnackbar,
      linkToEntity,
      onSuccess,
      selectedFiles,
      t,
      updateDocument,
    ],
  );

  const onSaveClick = useCallback(() => {
    void form.handleSubmit(values => submitUpload(values))();
  }, [form, submitUpload]);

  const scopeValue = form.watch('scope');
  const categoryValue = form.watch('category');

  const categorySelectOptions = useMemo(
    () =>
      scopeValue === DocumentScope.Company ? companyCategories : personalCategories,
    [companyCategories, personalCategories, scopeValue],
  );

  const subcategorySelectOptions = useMemo(() => {
    return getSubcategoriesForCategory(scopeValue, categoryValue);
  }, [categoryValue, getSubcategoriesForCategory, scopeValue]);

  const filesSummaryLabel = t('documents.upload-drawer.files-summary', {
    ns: 'components',
    count: selectedFiles.length,
    size: batchSizeLabel,
    limit: batchLimitLabel,
  });

  return {
    t,
    DrawerRenderer,
    ...drawerControls,
    form,
    selectedFiles,
    fileError,
    fileErrorMessage: getFileErrorMessage(fileError),
    isDragging,
    fileInputRef,
    filesSummaryLabel,
    isSaveDisabled,
    isSubmitting,
    categorySelectOptions,
    subcategorySelectOptions,
    currencySelectOptions: currencies,
    languageSelectOptions: languages,
    onRequestClose,
    onFileInputChange,
    onDrop,
    onDragOver,
    onDragLeave,
    onSelectFileClick,
    onRemoveFile,
    onScopeChange,
    onCategoryChange,
    onSaveClick,
  };
};
